"use client";

import { useState, useEffect, useRef } from "react";
import { useSearchParams } from "next/navigation";
import Image from "next/image";
import Link from "next/link";
import { cn } from "@/lib/utils";
import { useStoredUser } from "@/hooks/useStoredUser";
import { convertFileToJpegDataUrl } from "@/lib/imageToJpeg";
import { addBagItem, makeCustomOrderBagItem, DEFAULT_CUSTOM_ORDER_PRICE } from "@/hooks/useBag";

// ─── Types ──────────────────────────────────────────────────────────────────

type PieceSummary = {
  id: string;
  itemName: string;
  pictureUrl: string;
  type: string;
  metal: string;
  stone: string;
};

type CustomRequestText = {
  eyebrow: string;
  heading: string;
  description: string;
  referencePieceLabel: string;
  pieceTypeLabel: string;
  pieceTypeAutoDetectedSuffix: string;
  descriptionLabel: string;
  descriptionPlaceholder: string;
  goldColorLabel: string;
  stonePreferenceLabel: string;
  byStoneNameLabel: string;
  byColorLabel: string;
  ringSizeLabel: string;
  necklaceLengthLabel: string;
  braceletLengthLabel: string;
  contactInfoLabel: string;
  namePlaceholder: string;
  emailPlaceholder: string;
  submitButtonText: string;
  submittingButtonText: string;
  footerNote: string;
  successHeading: string;
  successMessage: string;
  successButtonText: string;
  successButtonLink: string;
  errorEmailRequired: string;
  errorNameRequired: string;
  errorGeneric: string;
};

type Props = {
  pieces: PieceSummary[];
  text: CustomRequestText;
  ownerActive?: boolean;
};

// ─── Constants ──────────────────────────────────────────────────────────────

const PIECE_TYPES = ["Ring", "Necklace", "Earrings", "Bracelet"] as const;

const GOLD_COLORS = [
  { value: "Rose Gold", label: "Rose Gold", swatch: "bg-gradient-to-br from-rose-300 to-rose-400" },
  { value: "White Gold", label: "White Gold", swatch: "bg-gradient-to-br from-slate-100 to-slate-200 border border-slate-300" },
  { value: "Yellow Gold", label: "Yellow Gold", swatch: "bg-gradient-to-br from-yellow-300 to-yellow-500" },
] as const;

const STONE_OPTIONS = [
  "Diamond", "Emerald", "Ruby", "Sapphire", "Onyx", "Paraiba",
  "Amethyst", "Aquamarine", "Tanzanite", "Garnet", "Opal",
  "Pearl", "Tourmaline", "Alexandrite", "Topaz", "Citrine",
  "Rubelite", "Peridot", "Black Diamond",
] as const;

const COLOR_SWATCHES = [
  { label: "Blue", color: "bg-blue-500" },
  { label: "Green", color: "bg-emerald-500" },
  { label: "Red", color: "bg-red-500" },
  { label: "Pink", color: "bg-pink-400" },
  { label: "Purple", color: "bg-purple-500" },
  { label: "White", color: "bg-white border border-slate-300" },
  { label: "Black", color: "bg-slate-900" },
  { label: "Yellow", color: "bg-yellow-400" },
  { label: "Orange", color: "bg-orange-400" },
  { label: "Clear", color: "bg-gradient-to-br from-white to-slate-100 border border-slate-200" },
] as const;

const RING_SIZES = [
  "4", "4.5", "5", "5.5", "6", "6.5", "7", "7.5",
  "8", "8.5", "9", "9.5", "10", "10.5", "11", "11.5", "12",
] as const;

const LENGTHS = [
  { value: '14"', label: '14" - Choker' },
  { value: '16"', label: '16" - Standard' },
  { value: '18"', label: '18" - Princess' },
  { value: '20"', label: '20" - Matinee' },
  { value: '24"', label: '24" - Opera' },
  { value: '30"', label: '30" - Rope' },
  { value: '36"', label: '36" - Extra Long' },
] as const;

const BRACELET_LENGTHS = [
  { value: '6"', label: '6" - Petite' },
  { value: '6.5"', label: '6.5" - Small' },
  { value: '7"', label: '7" - Standard' },
  { value: '7.5"', label: '7.5" - Medium' },
  { value: '8"', label: '8" - Large' },
  { value: '8.5"', label: '8.5" - Extra Large' },
] as const;

// ─── Helpers ────────────────────────────────────────────────────────────────

function detectPieceType(type: string): string | null {
  const lower = type.toLowerCase();
  if (/\bring\b/.test(lower)) return "Ring";
  if (lower.includes("necklace")) return "Necklace";
  if (lower.includes("bracelet")) return "Bracelet";
  if (lower.includes("earring")) return "Earrings";
  return null;
}

// ─── Sub-components ─────────────────────────────────────────────────────────

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <p className="text-[10px] font-semibold uppercase tracking-[0.3em] text-slate-500">
      {children}
    </p>
  );
}

function SelectableCard({
  active,
  onClick,
  children,
  className,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "flex items-center gap-2.5 rounded-sm border px-4 py-2.5 text-sm transition",
        active
          ? "border-royal bg-royal/5 text-royal"
          : "border-slate-200 bg-white text-slate-600 hover:border-slate-400 hover:text-slate-900",
        className,
      )}
    >
      {children}
    </button>
  );
}

// ─── Main Component ─────────────────────────────────────────────────────────

export function CustomRequestForm({ pieces, text, ownerActive = false }: Props) {
  const searchParams = useSearchParams();
  const preselectedId = searchParams.get("piece");
  const storedUser = useStoredUser();

  // ── State ─────────────────────────────────────────────────────────────────
  const [selectedPiece, setSelectedPiece] = useState<PieceSummary | null>(null);
  const [description, setDescription] = useState("");
  const [goldColor, setGoldColor] = useState("");
  const [stoneMode, setStoneMode] = useState<"stone" | "color">("stone");
  const [selectedStone, setSelectedStone] = useState("");
  const [selectedStoneColor, setSelectedStoneColor] = useState("");
  const [pieceType, setPieceType] = useState("");
  const [ringSize, setRingSize] = useState("");
  const [length, setLength] = useState("");
  const [firstName, setFirstName] = useState("");
  const [email, setEmail] = useState("");
  const [adding, setAdding] = useState(false);
  const [added, setAdded] = useState(false);
  const [error, setError] = useState("");
  const [inspirationPhoto, setInspirationPhoto] = useState<{ dataUrl: string; filename: string; previewUrl: string } | null>(null);
  const [photoError, setPhotoError] = useState("");
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [ownerPrice, setOwnerPrice] = useState("");
  const [ownerAdded, setOwnerAdded] = useState(false);

  // ── Pre-fill contact info from stored user ──────────────────────────────
  useEffect(() => {
    if (!storedUser) return;
    if (storedUser.email && !email) setEmail(storedUser.email);
    if (storedUser.firstName && !firstName) setFirstName(storedUser.firstName);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storedUser]);

  const hasStoredContact = Boolean(storedUser?.email && storedUser?.firstName);

  // ── Pre-select piece from query param ─────────────────────────────────────
  useEffect(() => {
    if (preselectedId) {
      const piece = pieces.find((p) => p.id === preselectedId);
      if (piece) {
        setSelectedPiece(piece);
        const detected = detectPieceType(piece.type);
        if (detected) setPieceType(detected);
      }
    }
  }, [preselectedId, pieces]);

  // ── Derived ───────────────────────────────────────────────────────────────
  const effectiveType = pieceType;
  const showRingSize = effectiveType === "Ring";
  const showNecklaceLength = effectiveType === "Necklace";
  const showBraceletLength = effectiveType === "Bracelet";

  // ── Photo handling ────────────────────────────────────────────────────────
  const handlePhotoChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    setPhotoError("");
    if (!file.type.startsWith("image/")) {
      setPhotoError("Please choose an image file.");
      return;
    }
    try {
      const { dataUrl, filename } = await convertFileToJpegDataUrl(file);
      // ~4MB soft cap on the base64 payload to stay under the Vercel body limit
      if (dataUrl.length > 4_500_000) {
        setPhotoError("This image is too large — please choose a smaller photo.");
        return;
      }
      if (inspirationPhoto?.previewUrl) {
        URL.revokeObjectURL(inspirationPhoto.previewUrl);
      }
      setInspirationPhoto({ dataUrl, filename, previewUrl: URL.createObjectURL(file) });
    } catch (err) {
      console.error("[CustomRequest] photo conversion failed", err);
      setPhotoError("We couldn't process that image. Please try another.");
    }
  };

  const clearPhoto = () => {
    if (inspirationPhoto?.previewUrl) {
      URL.revokeObjectURL(inspirationPhoto.previewUrl);
    }
    setInspirationPhoto(null);
    setPhotoError("");
  };

  useEffect(() => {
    return () => {
      if (inspirationPhoto?.previewUrl) {
        URL.revokeObjectURL(inspirationPhoto.previewUrl);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Build structured custom-order details from the current selections ──────
  const buildCustomOrderInput = (photoUrl?: string) => {
    const stone = stoneMode === "stone" ? selectedStone : "";
    const stoneColor = stoneMode === "color" ? selectedStoneColor : "";
    const size = showRingSize ? ringSize : showNecklaceLength || showBraceletLength ? length : "";
    return {
      name: firstName.trim(),
      email: email.trim(),
      description: description.trim(),
      referencePiece: selectedPiece?.itemName || "",
      pieceType,
      goldColor,
      stone,
      stoneColor,
      size,
      photoUrl,
    };
  };

  // Upload the inspiration photo (if any) and return its public URL. Converts the
  // already-JPEG data URL back to a Blob for the multipart POST. Throws on failure
  // so the caller surfaces an error instead of silently dropping the photo.
  const uploadPhoto = async (): Promise<string | undefined> => {
    if (!inspirationPhoto) return undefined;
    const blob = await (await fetch(inspirationPhoto.dataUrl)).blob();
    const form = new FormData();
    form.append(
      "file",
      new File([blob], inspirationPhoto.filename || "inspiration.jpg", { type: blob.type })
    );
    const res = await fetch("/api/custom-request/photo", { method: "POST", body: form });
    const data = (await res.json().catch(() => null)) as { url?: string; error?: string } | null;
    if (!res.ok || !data?.url) throw new Error(data?.error || "Photo upload failed");
    return data.url;
  };

  // ── Customer: add a priceless-by-default ($300) custom line to the bag ──────
  const handleAddToCart = async () => {
    setError("");
    setAdding(true);
    try {
      const photoUrl = await uploadPhoto();
      addBagItem(
        makeCustomOrderBagItem({
          priceDollars: DEFAULT_CUSTOM_ORDER_PRICE,
          ...buildCustomOrderInput(photoUrl),
        })
      );
      setAdded(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not add to cart. Please try again.");
    } finally {
      setAdding(false);
    }
  };

  // ── Owner: add a priced custom line (uses the typed price + full spec) ──────
  const handleOwnerAddToBag = async () => {
    const priceDollars = Number(ownerPrice.replace(/[^0-9.]/g, "")) || 0;
    if (priceDollars <= 0) return;
    setError("");
    setAdding(true);
    try {
      const photoUrl = await uploadPhoto();
      addBagItem(makeCustomOrderBagItem({ priceDollars, ...buildCustomOrderInput(photoUrl) }));
      setOwnerAdded(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not add to bag.");
    } finally {
      setAdding(false);
    }
  };

  // ── Form ──────────────────────────────────────────────────────────────────
  return (
    <div className="-mt-[48px] sm:-mt-[84px]">
      <div className="mx-auto flex min-h-[calc(100vh-60px)] w-full max-w-3xl flex-col gap-10 px-4 pt-24 pb-16 text-slate-900">
        {/* Header */}
        <header className="text-center space-y-3">
          <p className="text-xs uppercase tracking-[0.4em] text-slate-500">
            {text.eyebrow}
          </p>
          <h1 className="mx-auto max-w-2xl text-3xl font-semibold uppercase leading-tight md:text-5xl font-[var(--font-display)]">
            {text.heading}
          </h1>
          <p className="text-sm text-slate-600 max-w-lg mx-auto">
            {text.description}
          </p>
        </header>

        {/* Form */}
        <div className="space-y-8 border border-slate-200 bg-white p-6 sm:p-8 shadow-soft">
          {/* ── Selected piece ──────────────────────────────────────────── */}
          {selectedPiece && (
            <div className="space-y-3">
              <SectionLabel>{text.referencePieceLabel}</SectionLabel>
              <div className="flex items-center gap-4 rounded-sm border border-slate-200 p-3">
                {selectedPiece.pictureUrl && (
                  <div className="relative h-20 w-20 flex-shrink-0 overflow-hidden bg-white">
                    <Image
                      src={selectedPiece.pictureUrl}
                      alt={selectedPiece.itemName || "Selected piece"}
                      fill
                      className="object-contain"
                      sizes="80px"
                    />
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-slate-900 truncate">
                    {selectedPiece.itemName}
                  </p>
                  <p className="text-xs text-slate-500 truncate">
                    {[selectedPiece.type, selectedPiece.metal, selectedPiece.stone]
                      .filter(Boolean)
                      .join(" / ")}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setSelectedPiece(null);
                    setPieceType("");
                  }}
                  className="flex-shrink-0 p-1 text-slate-400 hover:text-slate-600 transition"
                  aria-label="Remove selected piece"
                >
                  <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
              </div>
            </div>
          )}

          {/* ── Piece type ──────────────────────────────────────────────── */}
          <div className="space-y-3">
            <SectionLabel>
              {text.pieceTypeLabel} {selectedPiece ? text.pieceTypeAutoDetectedSuffix : ""}
            </SectionLabel>
            <div className="flex flex-wrap gap-2">
              {PIECE_TYPES.map((type) => (
                <SelectableCard
                  key={type}
                  active={pieceType === type}
                  onClick={() => {
                    setPieceType(pieceType === type ? "" : type);
                    setRingSize("");
                    setLength("");
                  }}
                >
                  {type}
                </SelectableCard>
              ))}
            </div>
          </div>

          {/* ── Description ─────────────────────────────────────────────── */}
          <div className="space-y-3">
            <SectionLabel>{text.descriptionLabel}</SectionLabel>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={text.descriptionPlaceholder}
              className="min-h-[120px] w-full rounded-sm border border-slate-300 bg-white px-4 py-3 text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-royal/20 focus:border-royal/40 transition"
            />
          </div>

          {/* ── Inspiration photo ──────────────────────────────────────── */}
          <div className="space-y-3">
            <SectionLabel>Inspiration photo (optional)</SectionLabel>
            {inspirationPhoto ? (
              <div className="flex items-center gap-4 rounded-sm border border-slate-200 p-3">
                <div className="relative h-20 w-20 flex-shrink-0 overflow-hidden bg-white">
                  {/* eslint-disable-next-line @next/next/no-img-element */}
                  <img src={inspirationPhoto.previewUrl} alt="Inspiration preview" className="h-full w-full object-cover" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold text-slate-900">{inspirationPhoto.filename}</p>
                  <p className="text-xs text-slate-500">Converted to JPG</p>
                </div>
                <button
                  type="button"
                  onClick={clearPhoto}
                  className="flex-shrink-0 p-1 text-slate-400 transition hover:text-slate-600"
                  aria-label="Remove photo"
                >
                  <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="flex w-full items-center justify-center gap-2 rounded-sm border border-dashed border-slate-300 bg-white px-4 py-3 text-sm text-slate-500 transition hover:border-slate-400 hover:text-slate-700"
              >
                <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="17 8 12 3 7 8" />
                  <line x1="12" y1="3" x2="12" y2="15" />
                </svg>
                Upload an inspiration photo
              </button>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handlePhotoChange}
              className="hidden"
            />
            {photoError && <p className="text-xs text-red-600">{photoError}</p>}
          </div>

          {/* ── Gold color ──────────────────────────────────────────────── */}
          <div className="space-y-3">
            <SectionLabel>{text.goldColorLabel}</SectionLabel>
            <div className="flex flex-wrap gap-3">
              {GOLD_COLORS.map((gc) => (
                <SelectableCard
                  key={gc.value}
                  active={goldColor === gc.value}
                  onClick={() => setGoldColor(goldColor === gc.value ? "" : gc.value)}
                >
                  <span
                    className={cn("h-5 w-5 rounded-full flex-shrink-0", gc.swatch)}
                    aria-hidden="true"
                  />
                  {gc.label}
                </SelectableCard>
              ))}
            </div>
          </div>

          {/* ── Stone preference ─────────────────────────────────────────── */}
          <div className="space-y-4">
            <SectionLabel>{text.stonePreferenceLabel}</SectionLabel>

            {/* Toggle between stone name and color */}
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => {
                  setStoneMode("stone");
                  setSelectedStoneColor("");
                }}
                className={cn(
                  "rounded-sm border px-4 py-2 text-xs font-semibold uppercase tracking-[0.2em] transition",
                  stoneMode === "stone"
                    ? "border-royal bg-royal text-white"
                    : "border-slate-200 text-slate-500 hover:border-slate-400 hover:text-slate-700",
                )}
              >
                {text.byStoneNameLabel}
              </button>
              <button
                type="button"
                onClick={() => {
                  setStoneMode("color");
                  setSelectedStone("");
                }}
                className={cn(
                  "rounded-sm border px-4 py-2 text-xs font-semibold uppercase tracking-[0.2em] transition",
                  stoneMode === "color"
                    ? "border-royal bg-royal text-white"
                    : "border-slate-200 text-slate-500 hover:border-slate-400 hover:text-slate-700",
                )}
              >
                {text.byColorLabel}
              </button>
            </div>

            {stoneMode === "stone" ? (
              <div className="flex flex-wrap gap-2">
                {STONE_OPTIONS.map((stone) => (
                  <SelectableCard
                    key={stone}
                    active={selectedStone === stone}
                    onClick={() =>
                      setSelectedStone(selectedStone === stone ? "" : stone)
                    }
                  >
                    {stone}
                  </SelectableCard>
                ))}
              </div>
            ) : (
              <div className="flex flex-wrap gap-3">
                {COLOR_SWATCHES.map((cs) => (
                  <SelectableCard
                    key={cs.label}
                    active={selectedStoneColor === cs.label}
                    onClick={() =>
                      setSelectedStoneColor(
                        selectedStoneColor === cs.label ? "" : cs.label,
                      )
                    }
                  >
                    <span
                      className={cn(
                        "h-5 w-5 rounded-full flex-shrink-0",
                        cs.color,
                      )}
                      aria-hidden="true"
                    />
                    {cs.label}
                  </SelectableCard>
                ))}
              </div>
            )}
          </div>

          {/* ── Ring size ────────────────────────────────────────────────── */}
          {showRingSize && (
            <div className="space-y-3">
              <SectionLabel>{text.ringSizeLabel}</SectionLabel>
              <div className="flex flex-wrap gap-2">
                {RING_SIZES.map((size) => (
                  <SelectableCard
                    key={size}
                    active={ringSize === size}
                    onClick={() => setRingSize(ringSize === size ? "" : size)}
                    className="px-3 py-2 min-w-[48px] justify-center"
                  >
                    {size}
                  </SelectableCard>
                ))}
              </div>
            </div>
          )}

          {/* ── Necklace length ──────────────────────────────────────────── */}
          {showNecklaceLength && (
            <div className="space-y-3">
              <SectionLabel>{text.necklaceLengthLabel}</SectionLabel>
              <div className="flex flex-wrap gap-2">
                {LENGTHS.map((l) => (
                  <SelectableCard
                    key={l.value}
                    active={length === l.value}
                    onClick={() => setLength(length === l.value ? "" : l.value)}
                  >
                    {l.label}
                  </SelectableCard>
                ))}
              </div>
            </div>
          )}

          {/* ── Bracelet length ──────────────────────────────────────────── */}
          {showBraceletLength && (
            <div className="space-y-3">
              <SectionLabel>{text.braceletLengthLabel}</SectionLabel>
              <div className="flex flex-wrap gap-2">
                {BRACELET_LENGTHS.map((l) => (
                  <SelectableCard
                    key={l.value}
                    active={length === l.value}
                    onClick={() => setLength(length === l.value ? "" : l.value)}
                  >
                    {l.label}
                  </SelectableCard>
                ))}
              </div>
            </div>
          )}

          {/* ── Divider ──────────────────────────────────────────────────── */}
          <div className="border-t border-slate-200" />

          {/* ── Contact info ─────────────────────────────────────────────── */}
          {hasStoredContact ? (
            <div className="space-y-2">
              <SectionLabel>{text.contactInfoLabel}</SectionLabel>
              <p className="text-sm text-slate-600">
                Sending as <span className="font-semibold text-slate-900">{firstName}</span>{" "}
                <span className="text-slate-400">({email})</span>
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              <SectionLabel>{text.contactInfoLabel}</SectionLabel>
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label htmlFor="custom-req-name" className="sr-only">Name</label>
                  <input
                    id="custom-req-name"
                    type="text"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    placeholder={text.namePlaceholder}
                    className="w-full rounded-sm border border-slate-300 bg-white px-4 py-3 text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-royal/20 focus:border-royal/40 transition"
                  />
                </div>
                <div>
                  <label htmlFor="custom-req-email" className="sr-only">Email</label>
                  <input
                    id="custom-req-email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={text.emailPlaceholder}
                    className="w-full rounded-sm border border-slate-300 bg-white px-4 py-3 text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-royal/20 focus:border-royal/40 transition"
                  />
                </div>
              </div>
            </div>
          )}

          {/* ── Error ────────────────────────────────────────────────────── */}
          {error && (
            <p className="text-sm text-red-600 text-center">{error}</p>
          )}

          {/* ── Owner: create custom order ──────────────────────────────── */}
          {ownerActive ? (
            <div className="mt-6 rounded-2xl border border-[#c4a668]/40 bg-[#faf6ec] p-4">
              <p className="text-xs font-semibold uppercase tracking-[0.3em] text-[#7a6320]">
                Owner: create custom order
              </p>
              <div className="mt-3 flex flex-wrap items-center gap-3">
                <input
                  type="text"
                  inputMode="decimal"
                  value={ownerPrice}
                  onChange={(e) => {
                    setOwnerPrice(e.target.value);
                    setOwnerAdded(false);
                  }}
                  placeholder="Price (USD)"
                  className="w-40 rounded-xl border border-slate-300 bg-white px-3 py-2 text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-200"
                />
                <button
                  type="button"
                  onClick={handleOwnerAddToBag}
                  className="inline-flex items-center justify-center rounded-sm bg-[#c4a668] px-6 py-3 text-sm font-semibold uppercase tracking-[0.3em] text-white transition hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-50"
                  disabled={(Number(ownerPrice.replace(/[^0-9.]/g, "")) || 0) <= 0 || ownerAdded}
                >
                  Add custom order to bag
                </button>
                {ownerAdded ? (
                  <span className="text-sm font-medium text-emerald-700">Added to bag ✓</span>
                ) : null}
              </div>
              <p className="mt-2 text-xs text-slate-500">
                Adds a priced Custom Order Request to the bag with the customer&apos;s contact info
                and the full request details (piece type, gold color, stone, size, notes, and
                photo) above. Check out from the bag to take a deposit.
              </p>
            </div>
          ) : null}

          {/* ── Submit ───────────────────────────────────────────────────── */}
          <button
            type="button"
            onClick={handleAddToCart}
            disabled={adding}
            className="w-full rounded-sm bg-royal px-6 py-3.5 text-sm font-semibold uppercase tracking-[0.3em] text-white shadow-soft transition hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:translate-y-0"
          >
            {adding ? "Adding…" : "Add to Cart"}
          </button>
          {added ? (
            <p className="mt-3 text-center text-sm font-medium text-emerald-700">
              Added to your bag ✓ —{" "}
              <Link href="/cart" className="underline">
                View bag
              </Link>
            </p>
          ) : null}
        </div>

        {/* Footer note */}
        <footer className="text-center text-xs uppercase tracking-[0.4em] text-slate-500">
          {text.footerNote}
        </footer>
      </div>
    </div>
  );
}
